import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  api,
  clearTokens,
  getAccessToken,
  getRefreshToken,
  getStoredUser,
  setTokens,
  StoredUser,
} from './api';

export interface LoginStepOneResult {
  status: 'totp_required' | 'enrollment_required';
  challenge_token: string;
  otpauth_uri?: string;
  qr_png_base64?: string;
  requires_totp_enrollment: boolean;
}

interface AuthContextValue {
  user: StoredUser | null;
  loginPassword: (email: string, password: string) => Promise<LoginStepOneResult>;
  verifyTotp: (
    email: string,
    code: string,
    challenge_token: string,
  ) => Promise<StoredUser>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<StoredUser | null>(() => getStoredUser());

  useEffect(() => {
    // Re-hydrate if a different tab logged in or out.
    function onStorage(e: StorageEvent) {
      if (e.key === 'jq_admin_user') setUser(getStoredUser());
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const loginPassword = useCallback(
    async (email: string, password: string): Promise<LoginStepOneResult> => {
      const r = await api().post('/auth/login', { email, password });
      return r.data as LoginStepOneResult;
    },
    [],
  );

  const verifyTotp = useCallback(
    async (
      email: string,
      code: string,
      challenge_token: string,
    ): Promise<StoredUser> => {
      const r = await api().post('/auth/totp/verify', {
        email,
        code,
        challenge_token,
      });
      const next: StoredUser = {
        email: r.data.user.email,
        totp_enrolled: !!r.data.user.totp_enrolled,
      };
      setTokens(r.data.access_token, r.data.refresh_token, next);
      setUser(next);
      return next;
    },
    [],
  );

  const logout = useCallback(async () => {
    const access = getAccessToken();
    const refresh = getRefreshToken();
    try {
      if (access) {
        await api().post(
          '/auth/logout',
          { refresh_token: refresh ?? undefined },
          { headers: { Authorization: `Bearer ${access}` } },
        );
      }
    } catch {
      // Best-effort: even if the network call fails, clear local state so
      // the user is logged out client-side.
    }
    clearTokens();
    setUser(null);
    window.location.href = '/login';
  }, []);

  const value = useMemo(
    () => ({ user, loginPassword, verifyTotp, logout }),
    [user, loginPassword, verifyTotp, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
